import { inject, Injectable } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import type { ActivatedRoute, UrlSegment } from '@angular/router';
import { Router, NavigationEnd } from '@angular/router';
import { isNil } from '@pct/ts-typeguard';
import type { Observable } from 'rxjs';
import { filter, map, merge } from 'rxjs';
import { ProjectContextService } from '../../features/project/project-context.service';

export interface Breadcrumb {
  label: string;
  url: string;
  raw: boolean; // true: nem fordítási kulcs, hanem tényleges szöveg
}

@Injectable({ providedIn: 'root' })
export class BreadcrumbService {
  private readonly router = inject(Router);
  private readonly projectContext = inject(ProjectContextService);

  // toObservable injection context-ben fut (service constructor), ez rendben van
  private readonly projectName$ = toObservable(this.projectContext.projectName);

  getBreadcrumbs$(): Observable<Breadcrumb[]> {
    return merge(this.router.events.pipe(filter(event => event instanceof NavigationEnd)), this.projectName$).pipe(
      map(() => this.collectBreadcrumbs()),
    );
  }

  private collectBreadcrumbs(): Breadcrumb[] {
    const crumbs: Breadcrumb[] = [];

    const getAllRoutes = (r: ActivatedRoute): void => {
      const snapshot = r.snapshot;

      if (snapshot.url.length > 0) {
        let label: string;
        let raw: boolean;

        if (snapshot.data['breadcrumbType'] === 'project') {
          // Dinamikus: signal-ból olvassuk, placeholder amíg tölt
          label = this.projectContext.projectName() ?? '…';
          raw = true;
        } else if (isNil(snapshot.data['session']?.name)) {
          label = snapshot.data['breadcrumb'] ?? '';
          raw = false;
        } else {
          label = snapshot.data['session'].name;
          raw = true;
        }

        let fullPath = '';
        let current: ActivatedRoute | null = r;
        while (current !== null) {
          const urlSegments = current.snapshot.url.map((u: UrlSegment) => u.path).join('/');
          if (urlSegments !== '') {
            fullPath = '/' + urlSegments + fullPath;
          }
          current = current.parent;
        }

        crumbs.push({ label, url: fullPath, raw });
      }

      if (r.firstChild !== null) {
        getAllRoutes(r.firstChild);
      }
    };

    getAllRoutes(this.router.routerState.root);
    return crumbs;
  }
}
